import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  FolderOpen,
  Building2,
  CheckSquare,
  Users,
  TrendingUp,
  Star,
  BarChart3,
  Monitor,
  UserCog,
  Clock,
  AlertTriangle,
  FileText,
  PanelLeftClose,
  PanelLeft,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui.store';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { api } from '@/services/api';
import type { LucideIcon } from 'lucide-react';

interface NavItem {
  label: string;
  icon: LucideIcon;
  href: string;
  badge?: 'tasks' | 'evaluations' | 'pending-users';
  permission?: string;
  anyPermission?: string[];
}

const mainNav: NavItem[] = [
  { label: 'My Dashboard', icon: LayoutDashboard, href: '/dashboard', badge: 'tasks' },
  { label: 'Groups/Projects', icon: FolderOpen, href: '/groups', permission: 'can_view_groups' },
  {
    label: 'Work Planning',
    icon: Building2,
    href: '/work',
    anyPermission: ['can_view_goals', 'can_view_sprints', 'can_view_teams'],
  },
  { label: 'Tasks', icon: CheckSquare, href: '/tasks', permission: 'can_view_tasks' },
  { label: 'People', icon: Users, href: '/people' },
  { label: 'Performance', icon: TrendingUp, href: '/performance', permission: 'can_view_performance' },
  { label: 'Evaluate', icon: Star, href: '/evaluate', badge: 'evaluations' },
  { label: 'Reports', icon: BarChart3, href: '/reports', permission: 'can_view_reports' },
];

const adminNav: NavItem[] = [
  { label: 'System Overview', icon: Monitor, href: '/admin/system-overview' },
  { label: 'User Management', icon: UserCog, href: '/admin/users', badge: 'pending-users' as any },
  { label: 'Timer Logs', icon: Clock, href: '/admin/timer-logs' },
  { label: 'Error Logs', icon: AlertTriangle, href: '/admin/error-logs' },
  { label: 'Audit Logs', icon: FileText, href: '/admin/audit-logs' },
];

function useIsMobile() {
  // Simple check - in production you might use a media query hook
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
}

export function Sidebar() {
  const location = useLocation();
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUIStore();
  const { isAuthenticated, isAdmin, hasPermission, hasAnyPermission } = useAuth();
  const isMobile = useIsMobile();

  // Fetch badge count for active tasks
  const { data: dashboardData } = useQuery({
    queryKey: ['sidebar-dashboard-badge'],
    queryFn: async () => {
      const { data } = await api.get('/dashboard');
      return data.data;
    },
    enabled: isAuthenticated,
    staleTime: 120_000,
    refetchInterval: 120_000,
  });

  // Fetch pending evaluations count
  const { data: evalData } = useQuery({
    queryKey: ['sidebar-eval-badge'],
    queryFn: async () => {
      const { data } = await api.get('/evaluations/pending');
      return data.data;
    },
    enabled: isAuthenticated,
    staleTime: 120_000,
    refetchInterval: 120_000,
  });

  // Fetch pending user count for admin badge
  const { data: pendingData } = useQuery({
    queryKey: ['admin-pending-count'],
    queryFn: async () => {
      const { data } = await api.get('/admin/users/pending-count');
      return data.data;
    },
    enabled: isAuthenticated && isAdmin,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const taskBadgeCount = dashboardData?.my_active_tasks ?? 0;
  const evalBadgeCount = Array.isArray(evalData) ? evalData.length : 0;
  const pendingUserCount = pendingData?.count ?? 0;

  function getBadgeCount(badge?: 'tasks' | 'evaluations' | 'pending-users'): number {
    if (badge === 'tasks') return taskBadgeCount;
    if (badge === 'evaluations') return evalBadgeCount;
    if (badge === 'pending-users') return pendingUserCount;
    return 0;
  }

  function isItemVisible(item: NavItem): boolean {
    if (item.permission) return hasPermission(item.permission);
    if (item.anyPermission) return hasAnyPermission(item.anyPermission);
    return true;
  }

  function isActive(href: string): boolean {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  }

  const sidebarContent = (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-14 items-center gap-2 border-b px-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/logo.png" alt="DALY" className="h-8 w-8 rounded-lg" />
            {(sidebarOpen || isMobile) && (
              <span className="text-lg font-bold text-foreground">DALY</span>
            )}
          </Link>
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <ScrollArea className="flex-1 py-2">
          {/* Main nav */}
          <nav className="space-y-1 px-2 overflow-visible">
            {mainNav.filter(isItemVisible).map((item) => {
              const active = isActive(item.href);
              const Icon = item.icon;
              const badgeCount = getBadgeCount(item.badge);
              const showBadge = badgeCount > 0;
              const collapsed = !sidebarOpen && !isMobile;

              return (
                <Tooltip key={item.href}>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.href}
                      onClick={() => isMobile && setSidebarOpen(false)}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors overflow-visible',
                        active
                          ? 'border-l-[3px] border-brand-primary bg-brand-primary/10 text-brand-primary font-medium'
                          : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                      )}
                    >
                      <div className="relative shrink-0">
                        <Icon className="h-4 w-4" />
                        {showBadge && collapsed && (
                          <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
                        )}
                      </div>
                      {(sidebarOpen || isMobile) && (
                        <span className="flex-1 min-w-0 truncate">{item.label}</span>
                      )}
                      {(sidebarOpen || isMobile) && showBadge && (
                        <span className="shrink-0 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[10px] font-bold text-white">
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                      )}
                    </Link>
                  </TooltipTrigger>
                  {collapsed && (
                    <TooltipContent side="right">
                      <div className="flex items-center gap-2">
                        {item.label}
                        {showBadge && (
                          <span className="rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                            {badgeCount}
                          </span>
                        )}
                      </div>
                    </TooltipContent>
                  )}
                </Tooltip>
              );
            })}
          </nav>

          {/* Admin section */}
          {isAdmin && (
            <>
              <Separator className="my-3 mx-2" />
              {(sidebarOpen || isMobile) && (
                <p className="mb-1 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Admin
                </p>
              )}
              <nav className="space-y-1 px-2 overflow-visible">
                {adminNav.map((item) => {
                  const active = isActive(item.href);
                  const Icon = item.icon;
                  const collapsed = !sidebarOpen && !isMobile;
                  const badgeCount = getBadgeCount(item.badge);
                  const showBadge = badgeCount > 0;

                  return (
                    <Tooltip key={item.href}>
                      <TooltipTrigger asChild>
                        <Link
                          to={item.href}
                          onClick={() => isMobile && setSidebarOpen(false)}
                          className={cn(
                            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors overflow-visible',
                            active
                              ? 'border-l-[3px] border-brand-primary bg-brand-primary/10 text-brand-primary font-medium'
                              : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                          )}
                        >
                          <div className="relative shrink-0">
                            <Icon className="h-4 w-4" />
                            {showBadge && collapsed && (
                              <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-background" />
                            )}
                          </div>
                          {(sidebarOpen || isMobile) && (
                            <span className="flex-1 min-w-0 truncate">{item.label}</span>
                          )}
                          {(sidebarOpen || isMobile) && showBadge && (
                            <span className="shrink-0 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-white">
                              {badgeCount > 99 ? '99+' : badgeCount}
                            </span>
                          )}
                        </Link>
                      </TooltipTrigger>
                      {collapsed && (
                        <TooltipContent side="right">{item.label}</TooltipContent>
                      )}
                    </Tooltip>
                  );
                })}
              </nav>
            </>
          )}
        </ScrollArea>

        {/* Collapse toggle - desktop only */}
        {!isMobile && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center"
              onClick={toggleSidebar}
            >
              {sidebarOpen ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeft className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </div>
    </TooltipProvider>
  );

  // Mobile: render as Sheet overlay
  if (isMobile) {
    return (
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-[260px] p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation</SheetTitle>
          </SheetHeader>
          {sidebarContent}
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: fixed sidebar
  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-30 flex flex-col border-r bg-background transition-all duration-200 overflow-y-auto overflow-x-visible',
        sidebarOpen ? 'w-60' : 'w-[60px]'
      )}
    >
      {sidebarContent}
    </aside>
  );
}
